/**
 * Credit Note Detail Page — /office/billing/credit-notes/<id>/
 *
 * Two pieces:
 *
 * 1. Send-credit-note modal — canonical dynamic-modal pattern
 *    (see ``form-modals.mdc`` and ``frontend/src/utils/ui/dynamic-modal.js``).
 *    The modal shell is static in ``credit_notes/detail.html``; the
 *    form (To/Subject/Message + draft warning) is fetched fresh on
 *    every open via GET AJAX, validated server-side, and re-painted
 *    inline on 422. On success the server returns
 *    ``{success: true, redirect_url}`` and the helper closes + redirects.
 *
 * 2. Confirm-then-action buttons (Delete Draft, Void) — handled with
 *    the global ``window.showConfirmModal`` helper. Triggers carry
 *    ``data-cn-confirm-action="submit-form|navigate"`` plus the
 *    standard confirm attrs (``-title``, ``-message``, ``-text``,
 *    ``-cancel-text``, ``-type``).
 */

import { createDynamicModal } from '../utils/ui/dynamic-modal.js';

let globalBound = false;
let sendCnHelper = null;

function ensureSendCnHelper() {
    if (sendCnHelper) return sendCnHelper;
    sendCnHelper = createDynamicModal({
        modalId: 'send-cn-modal',
        onSuccess: (data, helper) => {
            // afterClose so the soft-reload / redirect fires once the
            // leave transition is fully done — otherwise the Pattern B
            // partial swap destroys the backdrop mid-fade and the user
            // sees a "white flash".
            helper.close(() => {
                if (data && data.redirect_url) {
                    window.location.href = data.redirect_url;
                } else if (typeof window._softReload === 'function') {
                    window._softReload();
                } else {
                    window.location.reload();
                }
            });
        },
        errorLoadingMessage: 'Could not load the send form. Please try again.',
    });
    return sendCnHelper;
}

function handleDelegatedClick(evt) {
    const t = evt.target;
    if (!t || !t.closest) return;

    const sendBtn = t.closest('[data-send-cn-open]');
    if (sendBtn) {
        evt.preventDefault();
        const modalEl = document.getElementById('send-cn-modal');
        if (!modalEl) return;
        const url = sendBtn.getAttribute('data-send-cn-url')
            || modalEl.getAttribute('data-send-cn-url');
        if (!url) return;
        const title = sendBtn.getAttribute('data-send-cn-title') || '';
        const helper = ensureSendCnHelper();
        if (helper) helper.open(url, title);
        return;
    }

    const confirmBtn = t.closest('[data-cn-confirm-action]');
    if (confirmBtn) {
        if (typeof window.showConfirmModal !== 'function') return;
        const ds = confirmBtn.dataset;
        const action = ds.cnConfirmAction;
        window.showConfirmModal({
            title: ds.cnConfirmTitle || '',
            message: ds.cnConfirmMessage || '',
            confirmText: ds.cnConfirmText || 'Confirm',
            cancelText: ds.cnConfirmCancelText || 'Cancel',
            type: ds.cnConfirmType || 'default',
            onConfirm: function () {
                if (action === 'submit-form') {
                    const form = document.getElementById(ds.cnConfirmFormId);
                    if (form) form.submit();
                } else if (action === 'navigate') {
                    if (ds.cnConfirmUrl) window.location.href = ds.cnConfirmUrl;
                }
            },
        });
    }
}

function bindGlobalDelegation() {
    if (globalBound) return;
    globalBound = true;
    document.body.addEventListener('click', handleDelegatedClick);
}

export function initCreditNoteDetail(config) {
    config = config || {};
    const root = config.root || document.querySelector('[data-auto-init="creditNoteDetail"]');
    if (!root) return;
    // CRITICAL: invalidate the cached helper BEFORE recreating it.
    // ``createDynamicModal`` wires listeners onto the specific
    // ``#send-cn-modal`` <div> it found when first instantiated. After a
    // boosted navigation (list → detail → back → re-open another CN)
    // that <div> is detached and replaced by a fresh one with the same
    // id. Without this reset the cached helper still targets the
    // orphan node and ``Send by Email`` silently does nothing until a
    // hard refresh. See ``form-modals.mdc`` § "Boosted-nav module
    // caches" for the broader rule.
    sendCnHelper = null;
    bindGlobalDelegation();
    ensureSendCnHelper();
}

export function autoInitCreditNoteDetail() {
    const root = document.querySelector('[data-auto-init="creditNoteDetail"]');
    if (!root) return;
    initCreditNoteDetail({ root: root });
}
